import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { debounce } from '@mui/material/utils';

const googleApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

/** Places charges per request unless a search and its selection share a session token. */
const SEARCH_DEBOUNCE_MS = 400;

export const isGooglePlacesReady = () =>
    typeof window !== 'undefined' && Boolean(window.google?.maps?.places?.AutocompleteSuggestion);

/** Subscribers waiting for the places library; flushed once when it becomes usable. */
const readyListeners = new Set();
const notifyPlacesReady = () => {
    readyListeners.forEach((listener) => listener());
    readyListeners.clear();
};

/** Requests the places library and announces readiness only once the symbols we call exist. */
function requestPlacesLibrary(maps) {
    maps.importLibrary('places')
        .then(() => {
            if (isGooglePlacesReady()) {
                notifyPlacesReady();
                return;
            }
            console.error('Google places library loaded without AutocompleteSuggestion.');
        })
        .catch((error) => console.error('Error loading Google places library: ', error.message));
}

/**
 * Injects the Google Maps script once for the whole app.
 * `loading=async` means the script's load event fires before the places library is usable,
 * so readiness is announced only after importLibrary('places') resolves.
 */
export function loadGooglePlacesScript() {
    if (typeof window === 'undefined' || isGooglePlacesReady()) return;

    const maps = window.google?.maps;
    if (maps?.importLibrary) {
        // The script is alive but the library is not ready — ask again instead of re-injecting,
        // so one rejected request does not wedge autocomplete for the rest of the session.
        requestPlacesLibrary(maps);
        return;
    }

    if (document.querySelector('#google-maps')) return;

    const script = document.createElement('script');
    script.setAttribute('async', '');
    script.setAttribute('id', 'google-maps');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${googleApiKey}&libraries=places&loading=async&language=en`;
    script.addEventListener('load', () => {
        // `load` can fire before Google's own bootstrap finishes wiring up window.google.maps —
        // poll briefly instead of failing on the one moment it hasn't landed yet. A blocked or
        // hijacked response (blockers/proxies answering with an empty 200) never wires it up at
        // all, so this still gives up and lets the next mount retry from scratch.
        const POLL_INTERVAL_MS = 50;
        const POLL_TIMEOUT_MS = 3000;
        let waited = 0;

        const check = () => {
            const loadedMaps = window.google?.maps;
            if (loadedMaps?.importLibrary) {
                requestPlacesLibrary(loadedMaps);
                return;
            }
            waited += POLL_INTERVAL_MS;
            if (waited >= POLL_TIMEOUT_MS) {
                console.error('Google Maps script loaded without the maps namespace.');
                document.querySelector('#google-maps')?.remove();
                return;
            }
            setTimeout(check, POLL_INTERVAL_MS);
        };
        check();
    });
    script.addEventListener('error', () => {
        // A failed fetch leaves a dead tag that would block every later attempt; drop it so the
        // next mounted location field retries.
        console.error('Failed to load the Google Maps script.');
        document.querySelector('#google-maps')?.remove();
    });
    document.querySelector('head')?.appendChild(script);
}

/**
 * Tracks places-library readiness as React state so autocomplete effects re-run
 * when the script finishes loading mid-typing (instead of silently dropping the search).
 */
export function useGooglePlacesReady() {
    const [ready, setReady] = useState(isGooglePlacesReady);

    useEffect(() => {
        if (ready) return undefined;

        const listener = () => setReady(true);
        readyListeners.add(listener);
        // The library may have become ready between render and this effect.
        if (isGooglePlacesReady()) setReady(true);

        return () => {
            readyListeners.delete(listener);
        };
    }, [ready]);

    return ready;
}

/** Converts a new-Places-API AutocompleteSuggestion into the shape the autocompletes render. */
export function toPlaceOption(suggestion) {
    const prediction = suggestion.placePrediction;
    const mainMatches = prediction.mainText?.matches || [];

    return {
        description: prediction.text?.text || '',
        place_id: prediction.placeId,
        structured_formatting: {
            main_text: prediction.mainText?.text || '',
            secondary_text: prediction.secondaryText?.text || '',
            main_text_matched_substrings: mainMatches.map((match) => ({
                offset: match.startOffset || 0,
                length: match.endOffset - (match.startOffset || 0),
            })),
        },
    };
}

/** Options may be a suggestion or the plain string a field was saved with. */
export const placeOptionLabel = (option) => (typeof option === 'string' ? option : option?.description || '');

export const isSamePlaceOption = (option, value) => placeOptionLabel(option) === placeOptionLabel(value);

/** The pieces renderOption draws, read defensively so no option shape can crash the dropdown. */
export function placeOptionParts(option) {
    const structured = option && typeof option === 'object' ? option.structured_formatting : null;
    return {
        mainText: structured?.main_text || placeOptionLabel(option),
        matches: structured?.main_text_matched_substrings || [],
        secondaryText: structured?.secondary_text || '',
    };
}

/** Keeps the committed value at the head of the list so MUI can match it to the field value. */
function seedOptions(value) {
    const label = placeOptionLabel(value);
    if (!label) return [];
    return [
        {
            description: label,
            place_id: '',
            structured_formatting: { main_text: label, secondary_text: '', main_text_matched_substrings: [] },
        },
    ];
}

/**
 * Turns a geocoder result into the CITY,STATE,COUNTRY string location fields store.
 * One implementation for every surface, so the same place cannot be saved differently
 * depending on which screen it was typed into.
 */
export function geocodedLocationString(result) {
    const components = result?.address_components || [];
    const find = (type) => components.find((component) => component.types.includes(type));

    const country = find('country')?.short_name || '';
    const state = find('administrative_area_level_1')?.short_name || '';
    // Smaller towns report no locality, so fall back to the districts that carry the name.
    const cityComponent =
        find('locality') || find('administrative_area_level_3') || find('administrative_area_level_2');
    const city = cityComponent?.short_name || state;

    return (city === state ? `${city},${country}` : `${city},${state},${country}`).toUpperCase();
}

/**
 * Drives a Places autocomplete field: script loading, readiness, the debounced search and the
 * billing session. Callers keep only their own layout and what to do with the picked place.
 */
export function useGooglePlacesAutocomplete({ value, seedValue = true }) {
    const [inputValue, setInputValue] = useState(() => placeOptionLabel(value));
    const [options, setOptions] = useState([]);
    const ready = useGooglePlacesReady();
    const sessionTokenRef = useRef(null);
    /** Only a keystroke may start a billable search — MUI also reports selections and resets. */
    const typedRef = useRef(false);

    useEffect(() => {
        loadGooglePlacesScript();
    }, []);

    // Follow the committed value: MUI shows it in the input, so a stale search string here would
    // fetch for text the user can no longer see.
    useEffect(() => {
        typedRef.current = false;
        setInputValue(placeOptionLabel(value));
    }, [value]);

    const search = useMemo(
        () =>
            debounce((request, callback) => {
                window.google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request)
                    .then(({ suggestions }) => callback(suggestions))
                    .catch((error) => {
                        console.error('Error fetching place suggestions: ', error.message);
                        callback([]);
                    });
            }, SEARCH_DEBOUNCE_MS),
        []
    );

    useEffect(() => {
        let active = true;
        const seeded = seedValue ? seedOptions(value) : [];

        // Opening a field or picking a place is not a search; fetching there bills a session that
        // can never convert and refills a dropdown the user already closed.
        if (!typedRef.current || inputValue === '' || !ready) {
            setOptions(seeded);
            return undefined;
        }

        if (!sessionTokenRef.current) {
            sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
        }

        search({ input: inputValue, sessionToken: sessionTokenRef.current }, (suggestions) => {
            if (!active) return;
            const predictions = (suggestions || [])
                .filter((suggestion) => suggestion.placePrediction)
                .map(toPlaceOption);
            setOptions([...seeded, ...predictions]);
        });

        return () => {
            active = false;
            // Cancel the pending request so a cleared input or an unmounted field does not still
            // fire a billable Places call up to 400ms later.
            search.clear();
        };
    }, [value, inputValue, ready, search, seedValue]);

    const handleInputChange = useCallback((event, next, reason) => {
        typedRef.current = reason === 'input';
        setInputValue(next);
    }, []);

    /** Call once a place is picked: that session ended, so the next search opens a new one. */
    const endSession = useCallback(() => {
        typedRef.current = false;
        sessionTokenRef.current = null;
    }, []);

    return { options, inputValue, handleInputChange, endSession, ready };
}

export const handleRequiredState = (newValue, { keyname, dispatch, formAction, setState }) => {
    if (newValue === null) return;

    const geocoder = new window.google.maps.Geocoder();
    geocoder
        .geocode({ placeId: newValue?.place_id || '' })
        .then(({ results }) => {
            if (results[0] !== null) {
                const afterCity = geocodedLocationString(results[0]);

                // Dispatch to Redux if dispatch + formAction provided
                if (dispatch && formAction && keyname) {
                    dispatch(formAction({ [keyname]: afterCity }));
                }
                // Update local state if setState is provided
                if (setState) {
                    setState((prev) => ({ ...prev, [keyname]: afterCity }));
                }
            }
        })
        .catch((error) => {
            console.error('Error during geocoding: ', error.message);
        });
};
